/* eslint-disable max-lines -- one method per tprm-ui endpoint; splitting by area is a refactor of its own */
import { chunk, isNil, mapValues, merge, noop, omit, omitBy } from 'lodash';
import { stringify as buildQuery } from 'query-string';
import { PartialDeep } from 'type-fest';

import { ActivityHistorySystemTags, ActivityHistoryTagsMap } from '@evidentid/tprm-portal-lib/models/activity-history';
import { InsuranceApiKey, InsuranceWebhook, InsuranceWebhookInput, InsuranceWebhookType } from '@evidentid/tprm-portal-lib/models/api-settings';
import {
    GovernmentIssuedWcJurisdictions,
    TprmCredentialType,
    TprmRequirementDefinition,
    TprmRequestsConfig,
} from '@evidentid/tprm-portal-lib/models/common';
import {
    ContractExtractionDataResponse,
    ContractExtractionProgress,
} from '@evidentid/tprm-portal-lib/models/contract-extraction';
import { GoAppConfig } from '@evidentid/tprm-portal-lib/models/go-app';
import {
    CustomProperty, Entity, EntityInputApiModel, CustomPropertyValues, EntityStatistics, RawCategorizedEnumLabels,
    CustomPropertiesPostResponse, CustomPropertyUsage, RpConfiguration, UiConfig, EnumOptionUsage,
} from '@evidentid/tprm-portal-lib/models/dashboard';
import {
    Criterion, CriterionInput, CriterionMessage, CriterionTemplate, EntityRiskProfile, EffectiveRiskProfiles,
    RiskProfile, RiskProfileInput,
} from '@evidentid/tprm-portal-lib/models/decisioning';
import {
    AmRatingResult,
    DocumentDataReviewData,
} from '@evidentid/tprm-portal-lib/models/document-data-review';
import {
    ActionResolveInput as InsuranceActionResolveInput,
    ActionReview as InsuranceActionReview,
    ActionReviewsQuery as InsuranceActionReviewsQuery,
    RevertImpact,
} from '@evidentid/tprm-portal-lib/models/entity-actions-review';
import {
    CollateralEntity, CollateralMappingOptions, ComplianceStatus, EntityRequirementDetails, ExceptionInput,
    OpenVerification, OpenVerificationResponse, RequirementTypesPerCountry, TprmRequirementModel, TprmRequirementType,
} from '@evidentid/tprm-portal-lib/models/entity-details';
import { CreateEntityNotePayload, EntityNote } from '@evidentid/tprm-portal-lib/models/entity-notes';
import { ChatMessage } from '@evidentid/tprm-portal-lib/models/evie/ChatMessage.model';
import { EvieConversationApiResponse } from '@evidentid/tprm-portal-lib/models/evie/EvieConversationApiResponse.model';
import { EvieConversationsApiResponse } from '@evidentid/tprm-portal-lib/models/evie/EvieConversationsApiResponse.model';
import { SseEvent } from '@evidentid/tprm-portal-lib/models/evie/SseEvent.model';
import { KbArticle, KbCategory, KbSearchResult } from '@evidentid/tprm-portal-lib/models/kb';
import {
    NotificationConfiguration,
    NotificationConfigurationsResponse,
    NotificationType,
    TprmClientConfig,
    TprmClientConfigInput,
    VerificationRequest,
} from '@evidentid/tprm-portal-lib/models/notification-configuration';
import { ProductUpdate } from '@evidentid/tprm-portal-lib/models/product-updates';
import { RbacRoleDetails } from '@evidentid/tprm-portal-lib/models/rbac';
import { SsoGroupRuleView, SsoProvisioningView } from '@evidentid/tprm-portal-lib/models/sso-provisioning';
import { User, UserInput, UserSearchResult } from '@evidentid/tprm-portal-lib/models/user-management/User.model';
import { convertApiWebhookToViewData } from '@evidentid/tprm-portal-lib/utils/api-settings/convertApiWebhookToViewData';

import { convertUserDataToViewModel } from '../converters/userConverter';
import {
    ActivityHistoryActionableTags, ActivityHistoryQuery, ActivityHistoryResult,
    EntityPatchResponse, EntityPostResponse, EntityUpsertResponse, SendDocumentRequestResponse,
    RpUsedAttributesData, RelyingPartySignature, TermsAndConditionsStatus, VerificationRequestEntry,
} from '../models';
import { PatchOperation } from '../models/common.model';
import {
    Branding,
    BrandingInput,
    Cadence,
    CadenceInput,
    ConfigRelyingParty,
    ConfigRelyingPartyInput,
    NotificationName,
    NotificationPreference,
    RpFulfillmentConfig,
} from '../models/config';

interface InsuranceInsuredsQuery {
    skip?: number;
    limit?: number;
    sort?: string | null;
    active?: boolean;
    paused?: boolean;
    search?: string | null;
    complianceStatus?: string;
    expiresBeforeOrOn?: string;
    expiresAfterOrOn?: string;
    displayName?: string;
    contactName?: string;
    contactEmail?: string;
    contactPhoneNumber?: string;
    producer?: string;
    customPropertyFilters?: Record<string, string>;
    collateralFieldFilters?: Record<string, string>;
}

interface InsuranceInsuredsSearchResult {
    insureds: Entity[];
    count: number;
}

interface InsuranceInsuredActionsSearchResult {
    actions: InsuranceActionReview[];
    count: number;
    // The server's true count across every page, independent of how many records this
    // response carries. Null when the caller did not request it (or, for a legacy caller,
    // when the backend omits it entirely).
    total: number | null;
}

interface UpsertOptions {
    strictProducerChecks?: boolean;
}

interface BatchUpsertOptions extends UpsertOptions {
    updateProgress: Function;
}

export interface EntityPatch {
    id: string;
    displayName?: PatchOperation<string>;
    nextExpiration?: PatchOperation<string | null>;
    complianceStatus?: PatchOperation<ComplianceStatus>;
    active?: PatchOperation<boolean>;
    paused?: PatchOperation<boolean>;
    insuredFields?: PatchOperation<CustomPropertyValues>;
}

type RequestMethod = <T = any>(
    method: string,
    requestUrl: string,
    data?: any,
    allowedAdditionalHttpStatuses?: number[],
) => Promise<T>;

type StreamingRequestMethod = (
    requestUrl: string,
    data: unknown,
    onEvent: (event: SseEvent) => void,
    options?: { signal?: AbortSignal },
) => Promise<void>;

type BinaryRequestMethod = (requestUrl: string) => Promise<Blob>;

function getTprmUiUrl(path: string): string {
    return encodeURI(`/tprm-ui${path}`);
}

function getTprmUiRpUrl(commonName: string, path: string): string {
    return encodeURI(`/tprm-ui/rps/${commonName}${path}`);
}

/** Maps one batch's 0-100 progress onto the slice of the whole operation it covers, so a
 * caller splitting work across batches sees a single bar rather than one per batch.
 */
function buildChunkProgressUpdate(
    updateProgress: Function, completedChunks: number, chunksInBatch: number, totalChunks: number,
): (progress: number) => void {
    const update = updateProgress || noop;
    return (progress: number) =>
        update((completedChunks + (progress / 100) * chunksInBatch) / totalChunks * 100);
}

export class TprmUiService {
    private readonly request: RequestMethod;
    private readonly streamingRequest: StreamingRequestMethod;
    private readonly binaryRequest: BinaryRequestMethod;

    public constructor(
        request: RequestMethod,
        streamingRequest: StreamingRequestMethod,
        binaryRequest: BinaryRequestMethod,
    ) {
        this.request = request;
        this.streamingRequest = streamingRequest;
        this.binaryRequest = binaryRequest;
    }

    public getTprmRequirements(): Promise<TprmRequirementDefinition[]> {
        return this.request('GET', getTprmUiUrl('/models/requirements'));
    }

    public getGovernmentIssuedWcJurisdictions(): Promise<GovernmentIssuedWcJurisdictions> {
        return this.request('GET', getTprmUiUrl('/models/government-issued-wc-jurisdictions'));
    }

    public getTprmCredentialTypes(): Promise<TprmCredentialType[]> {
        return this.request('GET', getTprmUiUrl('/models/credentials'));
    }

    public getProductUpdates(): Promise<ProductUpdate[]> {
        return this.request('GET', getTprmUiUrl('/product-updates'));
    }

    public getKbTree(): Promise<KbCategory[]> {
        return this.request('GET', getTprmUiUrl('/kb/tree'));
    }

    public getKbArticle(id: string): Promise<KbArticle> {
        return this.request('GET', getTprmUiUrl(`/kb/articles/${encodeURIComponent(id)}`));
    }

    public getKbArticleImage(articleId: string, attachmentId: string): Promise<Blob> {
        return this.binaryRequest(getTprmUiUrl(
            `/kb/articles/${encodeURIComponent(articleId)}/images/${encodeURIComponent(attachmentId)}`,
        ));
    }

    public searchKb(query: string): Promise<KbSearchResult[]> {
        return this.request('GET', `${getTprmUiUrl('/kb/search')}?q=${encodeURIComponent(query)}`);
    }

    public getProductUpdatesArchive(): Promise<ProductUpdate[]> {
        return this.request('GET', getTprmUiUrl('/product-updates/archive'));
    }

    public async dismissProductUpdate(id: string): Promise<void> {
        await this.request('POST', getTprmUiUrl(`/product-updates/${id}/dismiss`));
    }

    public async dismissAllProductUpdates(): Promise<void> {
        await this.request('POST', getTprmUiUrl('/product-updates/dismiss-all'));
    }

    public async getAvailableRelyingParties(): Promise<RelyingPartySignature[]> {
        return this.request('GET', `${getTprmUiUrl(`/rps`)}`);
    }

    public async getSsoProvisioning(commonName: string): Promise<SsoProvisioningView> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/sso-provisioning'));
    }

    public async putSsoProvisioningEnabled(commonName: string, enabled: boolean): Promise<void> {
        await this.request('PUT', getTprmUiRpUrl(commonName, '/sso-provisioning/enabled'), { enabled });
    }

    public async putSsoProvisioningRules(commonName: string, rules: SsoGroupRuleView[]): Promise<void> {
        await this.request('PUT', getTprmUiRpUrl(commonName, '/sso-provisioning/rules'), { rules });
    }

    public async getTprmEntities(commonName: string, options: InsuranceInsuredsQuery):
    Promise<InsuranceInsuredsSearchResult> {
        const customPropertyFilters = options.customPropertyFilters || {};
        const collateralFieldFilters = options.collateralFieldFilters || {};
        const qs = buildQuery({
            skip: 0,
            limit: 25,
            active: options.active,
            includeTotalValueInResponse: false,
            ...omit(options, 'customPropertyFilters', 'collateralFieldFilters'),
            ...customPropertyFilters,
            ...collateralFieldFilters,
        });
        const requestUrl = `${getTprmUiRpUrl(commonName, '/insureds')}?${qs}`;
        const { navigation, records } = await this.request('GET', requestUrl);
        return { count: navigation.count, insureds: records };
    }

    public async getTprmEntity(commonName: string, id: string): Promise<Entity> {
        const requestUrl = `${getTprmUiRpUrl(commonName, `/insureds/${id}`)}`;
        return this.request('GET', requestUrl);
    }

    public async getActivityHistory(
        commonName: string,
        entityId: string,
        query: ActivityHistoryQuery = {},
    ): Promise<ActivityHistoryResult> {
        const { entryType, tags, causedBy, ...rest } = query;
        const qs = buildQuery({
            ...rest,
            ...(entryType?.length ? { entryType: entryType.join(',') } : {}),
            ...(tags?.length ? { tags: tags.join(',') } : {}),
            ...(causedBy ? { causedBy } : {}),
        });
        const requestUrl = `${getTprmUiRpUrl(commonName, `/insureds/${entityId}/activity-history`)}?${qs}`;
        return this.request('GET', requestUrl);
    }

    public async getActivityHistoryActionableTags(
        commonName: string,
        entityId: string,
    ): Promise<ActivityHistoryActionableTags> {
        const requestUrl = getTprmUiRpUrl(commonName, `/insureds/${entityId}/activity-history/actionable-tags`);
        return this.request('GET', requestUrl);
    }

    public async getGlobalActivityHistory(
        commonName: string,
        query: Omit<ActivityHistoryQuery, 'tags'> = {},
    ): Promise<ActivityHistoryResult> {
        const { entryType, causedBy, ...rest } = query;
        const qs = buildQuery({
            ...rest,
            ...(entryType?.length ? { entryType: entryType.join(',') } : {}),
            ...(causedBy ? { causedBy } : {}),
        });
        return this.request('GET', `${getTprmUiRpUrl(commonName, '/activity-history')}?${qs}`);
    }

    public async getTprmCustomProperties(commonName: string): Promise<CustomProperty[]> {
        return await this.request<CustomProperty[]>('GET', getTprmUiRpUrl(commonName, '/config/insureds/fields'));
    }

    public async createTprmCustomProperty(
        commonName: string,
        field: { name: string, required: boolean, type: string, description: string | null, enumOptions?: string[] },
    ): Promise<CustomPropertiesPostResponse> {
        return this.request('POST', getTprmUiRpUrl(commonName, '/config/insureds/fields'), [ field ]);
    }

    public async updateTprmCustomProperty(
        commonName: string,
        fieldId: string,
        patch: { name: string, required: boolean, description: string | null, enumOptions?: string[] },
    ): Promise<CustomProperty> {
        return this.request('PATCH', getTprmUiRpUrl(commonName, `/config/insureds/fields/${fieldId}`), patch);
    }

    public async getEnumFieldOptionsUsage(
        commonName: string,
        fieldId: string,
    ): Promise<Record<string, EnumOptionUsage>> {
        return this.request('GET', getTprmUiRpUrl(commonName, `/config/insureds/fields/${fieldId}/options/usage`));
    }

    public async getCustomPropertyUsage(
        commonName: string,
        fieldId: string,
    ): Promise<CustomPropertyUsage> {
        return this.request('GET', getTprmUiRpUrl(commonName, `/config/insureds/fields/${fieldId}/usage`));
    }

    public async deleteTprmCustomProperty(commonName: string, fieldId: string): Promise<void> {
        return this.request('DELETE', getTprmUiRpUrl(commonName, `/config/insureds/fields/${fieldId}`));
    }

    public async createTprmEntities(commonName: string, insureds: EntityInputApiModel[]):
    Promise<EntityPostResponse> {
        return this.request('POST', getTprmUiRpUrl(commonName, '/insureds'), insureds);
    }

    public async batchCreateTprmEntities(
        commonName: string, insureds: EntityInputApiModel[], chunkSize: number,
        updateProgress: Function): Promise<PromiseSettledResult<EntityPostResponse>[]> {
        const insuredChunks = chunk(insureds, chunkSize || insureds.length);
        const promises: Promise<EntityPostResponse>[] = insuredChunks
            .map((insureds) => this.createTprmEntities(commonName, insureds));
        return this.batchRequests(promises, updateProgress);
    }

    /** `strictProducerChecks` also checks each producer list against the entity's own coverage types. */
    public async upsertTprmEntities(
        commonName: string, insureds: (EntityInputApiModel | Entity)[], options: UpsertOptions = {},
    ): Promise<EntityUpsertResponse> {
        const query = options.strictProducerChecks ? '?strictProducerChecks=true' : '';
        return this.request('PUT', `${getTprmUiRpUrl(commonName, '/insureds')}${query}`, insureds);
    }

    /** Sends the first chunk alone and waits for it to succeed before sending the rest. */
    public async batchUpsertTprmEntities(
        commonName: string, insureds: (EntityInputApiModel | Entity)[], chunkSize: number,
        { updateProgress, ...options }: BatchUpsertOptions,
    ): Promise<PromiseSettledResult<EntityUpsertResponse>[]> {
        const [ firstChunk, ...remainingChunks ] = chunk(insureds, chunkSize || 0);
        if (!firstChunk) {
            return this.batchRequests([], updateProgress);
        }
        const totalChunkCount = remainingChunks.length + 1;
        const probeResult = await this.batchRequests(
            [ this.upsertTprmEntities(commonName, firstChunk, options) ],
            buildChunkProgressUpdate(updateProgress, 0, 1, totalChunkCount),
        );
        if (probeResult[0].status === 'rejected') {
            // Stop here instead of risking a half-imported file if the rest would also fail.
            return probeResult;
        }
        const promises: Promise<EntityUpsertResponse>[] = remainingChunks
            .map((insureds) => this.upsertTprmEntities(commonName, insureds, options));
        const remainingResults = await this.batchRequests(
            promises,
            buildChunkProgressUpdate(updateProgress, 1, remainingChunks.length, totalChunkCount),
        );
        return [ ...probeResult, ...remainingResults ];
    }

    /** Reports which of the given producer emails use a blocked (personal/disposable) domain.
     *
     * The entity upsert refuses these anyway; asking first lets the UI flag the offending producer
     * while the user is still editing, which is what the network portal does during onboarding. */
    public async validateProducerEmails(commonName: string, emails: string[]): Promise<string[]> {
        const response = await this.request(
            'POST', getTprmUiRpUrl(commonName, '/producer-emails/validate'), { emails },
        );
        return response?.blockedEmails || [];
    }

    public async patchTprmEntity(commonName: string, patch: EntityPatch[]):
    Promise<EntityPatchResponse> {
        return await this.request('PATCH', getTprmUiRpUrl(commonName, '/insureds'), patch);
    }

    public async getTprmEntityExport(commonName: string): Promise<string> {
        return await this.request('GET', getTprmUiRpUrl(commonName, '/insureds/export'));
    }

    public async getTprmEntityStatistics(commonName: string): Promise<EntityStatistics> {
        return await this.request('GET', getTprmUiRpUrl(commonName, '/summaries/insureds'));
    }

    public async getRpConfiguration(commonName: string): Promise<RpConfiguration> {
        return await this.request('GET', getTprmUiRpUrl(commonName, '/config'));
    }

    public async updateUiConfig(commonName: string, config: UiConfig): Promise<void> {
        await this.request('PUT', getTprmUiRpUrl(commonName, '/config/ui'), config);
    }

    public async getTprmApiKey(commonName: string): Promise<InsuranceApiKey> {
        const data = await this.request('GET', getTprmUiRpUrl(commonName, '/verify-api-key'));
        return { accountName: commonName, key: data?.apiKey };
    }

    public async sendEntityDocumentRequest(
        commonName: string, entries: VerificationRequestEntry[], chunkSize: number,
        updateProgress: (progress: number) => void): Promise<SendDocumentRequestResponse[]> {
        const chunks = chunk(entries, chunkSize);
        const promises = chunks.map((entryChunk) =>
            this.request('POST', getTprmUiRpUrl(commonName, '/verifications'), entryChunk));
        const results = await this.batchRequests(promises, updateProgress);
        return results.map((result, index) => (result.status === 'fulfilled'
            ? result.value
            // A rejected chunk means its entities were never attempted. Reporting zeroes
            // throughout would show the user "nothing failed" for entities that never got a
            // request, so the failures are reported -- but `totalCount` stays zero, because it
            // counts entities whose call was answered and these had no answer. Callers subtract
            // it from the selection size to learn how many never went out; inflating it here
            // makes a send whose every call failed read as a complete success.
            : {
                totalCount: 0,
                successCount: 0,
                failureCount: chunks[index].length,
                successes: [],
                failures: chunks[index].map((entry) => ({
                    element: entry.insuredId,
                    error: {
                        error: 'Unknown',
                        reason: 'REQUEST_FAILED',
                        message: 'The request could not be sent. Please try again.',
                    },
                })),
            }));
    }

    public async getRpUsedAttributesData(commonName: string): Promise<RpUsedAttributesData> {
        return await this.request('GET', getTprmUiRpUrl(commonName, '/attributes'));
    }

    public async getTprmCollaterals(commonName: string, insuredId: string): Promise<CollateralEntity[]> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/insureds/${insuredId}/collaterals`));
    }

    public async getTprmCollateralMappingOptions(
        commonName: string,
        insuredId: string,
    ): Promise<CollateralMappingOptions[]> {
        return await this.request(
            'GET',
            getTprmUiRpUrl(commonName, `/insureds/${insuredId}/collaterals/mappings/options`),
        );
    }

    public async patchTprmCollaterals(
        commonName: string, insuredId: string, collateralsToPatch: Partial<CollateralEntity>[],
    ): Promise<void> {
        return await this.request(
            'PATCH',
            getTprmUiRpUrl(commonName, `/insureds/${insuredId}/collaterals`), collateralsToPatch,
        );
    }

    public async getTprmRequirementsDetailsList(commonName: string, insuredId: string):
    Promise<EntityRequirementDetails[]> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/insureds/${insuredId}/details`));
    }

    public async getTprmRoles(rpName: string): Promise<RbacRoleDetails[]> {
        return this.request('GET', getTprmUiRpUrl(rpName, '/roles'));
    }

    public async getTprmRiskProfiles(commonName: string):
    Promise<RiskProfile[]> {
        return await this.request('GET', getTprmUiRpUrl(commonName, '/decisioning/groups'));
    }

    public async getTprmRiskProfile(commonName: string, groupId: string):
    Promise<RiskProfile> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/decisioning/groups/${groupId}`));
    }

    public async getTprmEffectiveRiskProfiles(commonName: string):
    Promise<EffectiveRiskProfiles[]> {
        return await this.request('GET', getTprmUiRpUrl(commonName, '/decisioning/groups/effective'));
    }

    public async getTprmEntityRiskProfiles(commonName: string, insuredId: string):
    Promise<EntityRiskProfile[]> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/insureds/${insuredId}/groups`));
    }

    public async getTprmCriteria(commonName: string, groupId: string):
    Promise<Criterion[]> {
        return await this.request<Criterion[]>(
            'GET', getTprmUiRpUrl(commonName, `/decisioning/groups/${groupId}/criteria`));
    }

    public async getContractExtractionData(
        commonName: string,
        contractExtractionId: string,
    ): Promise<ContractExtractionDataResponse> {
        return await this.request(
            'GET', getTprmUiRpUrl(commonName, `/contract-extractions/${contractExtractionId}/data`));
    }

    public async getTprmRequirementTypeModels(): Promise<TprmRequirementModel[]> {
        return await this.request('GET', getTprmUiUrl('/models/coverages'));
    }

    public async getTprmCriteriaTemplatesPerCountry(rpcn: string, countryCode: string): Promise<CriterionTemplate[]> {
        return await this.request<CriterionTemplate[]>(
            'GET', getTprmUiRpUrl(rpcn, `/decisioning/criteria-templates?country=${countryCode}`));
    }

    public async postTprmCriterionForMessage(
        criterion: Criterion | CriterionInput,
        countryCode: string,
    ): Promise<CriterionMessage> {
        return await this.request('POST', getTprmUiUrl(`/models/${countryCode}/templates/criteria/messages`), criterion);
    }

    public async deleteTprmRiskProfile(commonName: string, groupId: string): Promise<void> {
        return await this.request('DELETE', getTprmUiRpUrl(commonName, `/decisioning/groups/${groupId}`));
    }

    public async createTprmRiskProfile(commonName: string, group: RiskProfileInput): Promise<RiskProfile> {
        return await this.request('POST', getTprmUiRpUrl(commonName, '/decisioning/groups'), group);
    }

    public async updateTprmRiskProfile(commonName: string, group: RiskProfile): Promise<RiskProfile> {
        return await this.request('PUT', getTprmUiRpUrl(commonName, `/decisioning/groups/${group.id}`), group);
    }

    public async createTprmCriteria(
        commonName: string, groupId: string,
        criteria: CriterionInput[],
    ): Promise<Criterion[]> {
        return await this.request(
            'POST', getTprmUiRpUrl(commonName, `/decisioning/groups/${groupId}/criteria`), criteria);
    }

    public async patchTprmCriteria(
        commonName: string, groupId: string,
        patches: any[]): Promise<any[]> {
        return await this.request('PATCH',
            getTprmUiRpUrl(commonName, `/decisioning/groups/${groupId}/criteria`), patches);
    }

    public async getTprmVerification(commonName: string, verificationId: string): Promise<VerificationRequest> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/verifications/${verificationId}`));
    }

    public async getTprmEntityVerifications(
        commonName: string, insuredId: string,
    ): Promise<VerificationRequest[]> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/insureds/${insuredId}/verifications`));
    }

    public async getOpenVerification(commonName: string, insuredId: string): Promise<OpenVerification | null> {
        const url = getTprmUiRpUrl(commonName, `/insureds/${insuredId}/open-verification`);
        return (await this.request<OpenVerificationResponse>('GET', url)).openVerification;
    }

    /** Rejects with a 409 body `{ reason }` when the verification can no longer be forced. */
    public async forceSubmission(commonName: string, insuredId: string, verificationId: string): Promise<void> {
        const path = `/insureds/${insuredId}/verifications/${verificationId}/force-submission`;
        await this.request('POST', getTprmUiRpUrl(commonName, path));
    }

    public async getRequirementTypesPerCountry():
    Promise<RequirementTypesPerCountry> {
        return await this.request('GET', getTprmUiUrl('/models/countries'));
    }

    public async getInsuranceConfig(commonName: string): Promise<TprmClientConfig> {
        return this.request('GET', getTprmUiRpUrl(commonName, ''));
    }

    public async updateInsuranceConfig(commonName: string, changes: Partial<TprmClientConfigInput>): Promise<void> {
        const payload = mapValues(changes, (newValue) => ({ op: 'REPLACE', newValue }));
        await this.request('PATCH', getTprmUiRpUrl(commonName, ''), payload);
    }

    public async getTprmRequestsConfig(commonName: string): Promise<TprmRequestsConfig> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/config/requests'));
    }

    public async updateTprmRequestsConfig(
        commonName: string,
        changes: PartialDeep<TprmRequestsConfig>,
    ): Promise<void> {
        const previousConfig = await this.getTprmRequestsConfig(commonName);
        const config = merge(previousConfig, changes);
        await this.request('PUT', getTprmUiRpUrl(commonName, '/config/requests'), config);
    }

    public async getGoAppConfig(commonName: string): Promise<GoAppConfig> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/config/go-app'));
    }

    public async updateGoAppConfig(commonName: string, config: GoAppConfig): Promise<GoAppConfig> {
        return this.request('PUT', getTprmUiRpUrl(commonName, '/config/go-app'), config);
    }

    public async getNotificationConfigurations(commonName: string): Promise<NotificationConfigurationsResponse> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/config/email-notifications'));
    }

    public async updateNotificationConfiguration(
        commonName: string,
        notificationType: NotificationType,
        config: NotificationConfiguration,
    ): Promise<NotificationConfiguration> {
        return this.request('PUT', getTprmUiRpUrl(commonName, `/config/email-notifications/${notificationType}`), config);
    }

    public async getInsuranceInsuredActions(
        commonName: string,
        queryOptions: InsuranceActionReviewsQuery): Promise<InsuranceInsuredActionsSearchResult> {
        const { actionTypes, ...filteredQuery } = omitBy(queryOptions, isNil) as InsuranceActionReviewsQuery;
        // The API takes the action discriminators as a single CSV `type` parameter.
        const typeParam = actionTypes && actionTypes.length > 0 ? { type: actionTypes.join(',') } : {};
        const qs = buildQuery({
            limit: 50,
            ...filteredQuery,
            ...typeParam,
        });

        const { navigation, records } =
            await this.request('GET', `${getTprmUiRpUrl(commonName, '/actions')}?${qs}`);

        // `count` is the number of records in this page; `total` is the server's count across
        // every page. The two agree whenever the whole result fits on one page, so a caller
        // wanting the real total must read `total`.
        return { count: navigation.count, total: navigation.total ?? null, actions: [ ...records ] };
    }

    public async getActionsCount(commonName: string): Promise<number> {
        const { count } = await this.request('GET', `${getTprmUiRpUrl(commonName, '/actions/count')}`);
        return count;
    }

    public getContractExtractionProgress(
        commonName: string,
        contractExtractionId: string,
    ): Promise<ContractExtractionProgress> {
        return this.request(
            'GET',
            getTprmUiRpUrl(commonName, `/contract-extractions/${contractExtractionId}/progress`),
        );
    }

    public async createActionResolve(
        commonName: string,
        actionId: string,
        resolve: InsuranceActionResolveInput): Promise<void> {
        await this.request('POST', getTprmUiRpUrl(commonName, `/actions/${actionId}/resolve`), resolve);
    }

    public async revertAction(
        commonName: string,
        actionId: string,
        reason?: string,
    ): Promise<InsuranceActionReview> {
        return this.request(
            'POST',
            getTprmUiRpUrl(commonName, `/actions/${actionId}/revert`),
            { reason: reason ?? null },
        );
    }

    public async getActionRevertImpact(commonName: string, actionId: string): Promise<RevertImpact> {
        return this.request('GET', getTprmUiRpUrl(commonName, `/actions/${actionId}/revert-impact`));
    }

    public async sendTestEmail(commonName: string,
        verificationType: string,
        to: string,
        cc: string[] = [],
    ): Promise<void> {
        return this.request('POST', getTprmUiRpUrl(commonName, '/verifications/emails/test'),
            { verificationType, recipient: to, carbonCopy: cc });
    }

    public async grantException(commonName: string, insuredId: string, exceptions: ExceptionInput[]): Promise<void> {
        await this.request('POST', getTprmUiRpUrl(commonName, `/insureds/${insuredId}/overrides`), exceptions);
    }

    public async removeExceptions(
        commonName: string,
        insuredId: string,
        items: { id: string, note?: string }[],
    ): Promise<void> {
        await this.request(
            'PATCH',
            getTprmUiRpUrl(commonName, `/insureds/${insuredId}/overrides`),
            items.map((item) => ({ op: 'DELETE', id: item.id, note: item.note })),
        );
    }

    public async getInsuranceEndorsementCategories(): Promise<string[]> {
        return await this.request('GET', getTprmUiUrl('/models/forms/categories'));
    }

    public async getCategorizedEnumLabels(_commonName: string): Promise<RawCategorizedEnumLabels> {
        return await this.request('GET', getTprmUiUrl('/models/enums'));
    }

    // User management section
    public async getUsers(
        commonName: string,
        options?: { page?: string | null, limit?: number },
    ): Promise<UserSearchResult> {
        const limit = Number(options?.limit) || 50;
        const qs = options?.page ? `page_token=${options.page}` : `page_size=${limit}`;
        const result = await this.request('GET', getTprmUiRpUrl(commonName, `/users?${qs}`));
        const [ , rawPrevToken ] = (result.previous_page || '').match(/page_token=([^&]+)/) || [ null, null ];
        const [ , rawNextToken ] = (result.next_page || '').match(/page_token=([^&]+)/) || [ null, null ];
        return {
            results: result.results.map(convertUserDataToViewModel),
            prev: rawPrevToken ? decodeURIComponent(rawPrevToken) : null,
            next: rawNextToken ? decodeURIComponent(rawNextToken) : null,
        };
    }

    public async createUser(
        commonName: string,
        data: { user: UserInput, loginUrl?: string, displayName?: string },
    ): Promise<User> {
        const { user } = await this.request('POST', getTprmUiRpUrl(commonName, '/users'), {
            user: data.user,
            app_login_page: data.loginUrl,
            requesting_resource_name: data.displayName,
        });
        return user;
    }

    public async updateUser(commonName: string, id: string, data: UserInput): Promise<User> {
        const user = await this.request('PUT', getTprmUiRpUrl(commonName, `/users/${id}`), data);
        return user;
    }

    public async deleteUser(commonName: string, id: string): Promise<void> {
        await this.request('DELETE', getTprmUiRpUrl(commonName, `/users/${id}`));
    }

    public async resendInvite(commonName: string, userId: string, loginUrl?: string): Promise<boolean> {
        const body = { app_login_page: loginUrl };
        const { status } = await this.request('POST', getTprmUiRpUrl(commonName, `/users/${userId}/resend-email`), body);
        return status === 'success';
    }

    public async getTprmSubmissionLink(
        commonName: string, entityId: string,
    ): Promise<{ submissionLink: string | null }> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/entities/${entityId}/submission-link`));
    }

    // End of User management section
    // TODO: PRODUCT-24440
    public async askChatAgent(
        commonName: string,
        question: string,
        sessionId: string,
        userEmail: string,
    ): Promise<any> {
        return await this.request(
            'POST',
            getTprmUiRpUrl(commonName, `/chatbot/completions?session_id=${sessionId}`),
            { question: { content: question } },
            { 'User-Email': userEmail } as any,
        );
    }

    // Config API methods migrated to tprm-ui
    public async getConfig(commonName: string): Promise<ConfigRelyingParty> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/config/profile'));
    }

    public async updateConfig(commonName: string, changes: Partial<ConfigRelyingPartyInput>): Promise<any> {
        return this.request('PUT', getTprmUiRpUrl(commonName, '/config/profile'), changes);
    }

    // TODO: make sure this is correct response and not amended in the new API
    public async getBrandings(commonName: string): Promise<Branding[]> {
        return (await this.request('GET', getTprmUiRpUrl(commonName, '/config/brandings'))).brandings;
    }

    public async getFulfillmentConfig(commonName: string): Promise<RpFulfillmentConfig> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/config/fulfillment'));
    }

    public async updateFulfillmentConfig(commonName: string, config: RpFulfillmentConfig): Promise<void> {
        return this.request('PUT', getTprmUiRpUrl(commonName, '/config/fulfillment'), config);
    }

    public async updateBranding(commonName: string, id: string, changes: Partial<BrandingInput>): Promise<Branding> {
        return this.request('PUT', getTprmUiRpUrl(commonName, `/config/brandings/${id}`), changes);
    }

    public async updateDefaultBranding(commonName: string, changes: Partial<BrandingInput>): Promise<Branding> {
        const brandings = await this.getBrandings(commonName);
        const defaultBranding = brandings.find((x) => x.isDefault) || brandings[0];
        if (!defaultBranding) {
            throw new Error('Default branding not found');
        }
        return this.updateBranding(commonName, defaultBranding._id, changes);
    }

    public async updateCadence(commonName: string, id: string, changes: Partial<CadenceInput>): Promise<Cadence> {
        return this.request('PUT', getTprmUiRpUrl(commonName, `/config/cadences/${id}`), changes);
    }

    public async getNotificationPreference(
        commonName: string, name: NotificationName,
    ): Promise<NotificationPreference> {
        return this.request('GET', getTprmUiRpUrl(commonName, `/config/notifications/${name}`));
    }

    // TODO: make sure that this indeed returns only eid-insurance hook types
    // return hookTypes?.filter((type: InsuranceWebhookType) => type.application.name === 'eid-insurance') || [];
    public async getWebhookTypes(): Promise<InsuranceWebhookType[]> {
        const { hookTypes } = await this.request('GET', getTprmUiUrl('/models/hook-types'));
        return hookTypes || [];
    }

    public async getInsuranceWebhooks(commonName: string): Promise<InsuranceWebhook[]> {
        const { webHooks } = await this.request('GET', getTprmUiRpUrl(commonName, '/config/webhooks')) || { webHooks: [] };
        return webHooks.map(convertApiWebhookToViewData);
    }

    public async createInsuranceWebhook(
        commonName: string, webhookInput: InsuranceWebhookInput,
    ): Promise<InsuranceWebhook> {
        const apiWebhook = await this.request('POST', getTprmUiRpUrl(commonName, '/config/webhooks'), webhookInput);
        return convertApiWebhookToViewData(apiWebhook);
    }

    public async patchInsuranceWebhook(commonName: string, webhook: InsuranceWebhook): Promise<unknown> {
        const apiWebhook = await this.request(
            'PATCH', getTprmUiRpUrl(commonName, `/config/webhooks/${webhook.id}`),
            { url: webhook.url },
        );
        return convertApiWebhookToViewData(apiWebhook);
    }

    public async deleteInsuranceWebhook(commonName: string, webhookId: string): Promise<unknown> {
        return await this.request('DELETE', getTprmUiRpUrl(commonName, `/config/webhooks/${webhookId}`));
    }

    public async getTermsAndConditionsSign(commonName: string): Promise<TermsAndConditionsStatus | null> {
        const result = await this.request('GET', getTprmUiRpUrl(commonName, '/terms-and-conditions'));
        return (result?.tcSigned) ? result : null;
    }

    public async signTermsAndConditions(commonName: string): Promise<void> {
        await this.request('PUT', getTprmUiRpUrl(commonName, '/terms-and-conditions'));
    }

    public async getDocumentDataReviewData(commonName: string, id: string): Promise<DocumentDataReviewData> {
        return await this.request('GET', getTprmUiRpUrl(commonName, `/document-data-reviews/${id}`));
    }

    public async getRequirementsSchema(
        commonName: string, requirements: TprmRequirementType[],
    ): Promise<Record<TprmRequirementType, any>> {
        const query = requirements && requirements.length > 0 ? `?for=${requirements.join(',')}` : '';
        return (await this.request(
            'GET', getTprmUiRpUrl(commonName, `/requirements/schemas${query}`) as any,
        )).requirements;
    }

    public async amBestSearch(
        commonName: string,
        carrierName: string,
        carrierNaicNumber: string,
    ): Promise<AmRatingResult[]> {
        const params = new URLSearchParams();
        if (carrierName) {
            params.set('carrier_name', carrierName);
        }
        if (carrierNaicNumber) {
            params.set('carrier_naic_number', carrierNaicNumber);
        }
        const url = `${getTprmUiRpUrl(commonName, `/document-data-reviews/am-best-lookup`)}?${params.toString()}`;
        try {
            const response = await this.request('GET', url);
            return response.matches;
        } catch (error) {
            console.error('Error fetching AM Best search results:', error);
            return [];
        }
    }

    private async batchRequests<T = any>(
        promises: Promise<T> [],
        updateProgress: Function): Promise<PromiseSettledResult<T>[]> {
        let requestsFinished = 0;
        const updateFunc = updateProgress || noop;
        const onRequestFinished = () => {
            updateFunc(++requestsFinished / promises.length * 100);
        };
        updateFunc(0);
        for (const promise of promises) {
            // `.finally()` returns a new promise that adopts the rejection of `promise` if it
            // rejects; nothing else holds a reference to it, so a rejected request would otherwise
            // surface as an unhandled rejection. `Promise.allSettled` below is what actually
            // observes each promise's outcome, so swallowing it here loses nothing.
            promise.finally(onRequestFinished).catch(noop);
        }
        return Promise.allSettled(promises);
    }

    public async getActivityHistoryTags(commonName: string): Promise<ActivityHistoryTagsMap> {
        return this.request('GET', getTprmUiRpUrl(commonName, '/activity-history/tags'));
    }

    public async getSystemTags(): Promise<ActivityHistorySystemTags> {
        return this.request('GET', getTprmUiUrl('/models/system-tags'));
    }

    public async patchResourceTags(
        commonName: string,
        entityId: string,
        payload: { resourceType: 'NOTE' | 'DOCUMENT', resourceId: string, tags: string[] },
    ): Promise<void> {
        const { resourceType, resourceId, tags } = payload;
        await this.request('PATCH',
            getTprmUiRpUrl(commonName, `/insureds/${entityId}/activity-history/resource/${resourceType}/${resourceId}/tags`),
            { tags });
    }

    public async createEntityNote(
        commonName: string,
        entityId: string,
        payload: CreateEntityNotePayload,
    ): Promise<EntityNote> {
        return this.request('POST', getTprmUiRpUrl(commonName, `/insureds/${entityId}/notes`), payload);
    }

    public async getEntityNote(
        commonName: string,
        entityId: string,
        noteId: string,
    ): Promise<EntityNote> {
        return this.request('GET', getTprmUiRpUrl(commonName, `/insureds/${entityId}/notes/${noteId}`));
    }

    public createEvieConversation(rpName: string): Promise<{ conversationId: string }> {
        return this.request('POST', getTprmUiRpUrl(rpName, '/evie/conversations'));
    }

    public getEvieConversations(rpName: string): Promise<EvieConversationsApiResponse> {
        return this.request('GET', getTprmUiRpUrl(rpName, '/evie/conversations'));
    }

    public getEvieConversation(rpName: string, conversationId: string): Promise<EvieConversationApiResponse> {
        return this.request('GET', getTprmUiRpUrl(rpName, `/evie/conversations/${conversationId}`));
    }

    public sendEvieMessage(rpName: string, conversationId: string, message: string): Promise<ChatMessage> {
        return this.request('POST', getTprmUiRpUrl(rpName, `/evie/conversations/${conversationId}/messages`), { message });
    }

    public sendEvieMessageStreaming(
        rpName: string,
        conversationId: string,
        message: string,
        options: { onEvent: (event: SseEvent) => void, signal?: AbortSignal },
    ): Promise<void> {
        const { onEvent, signal } = options;
        return this.streamingRequest(
            getTprmUiRpUrl(rpName, `/evie/conversations/${conversationId}/messages`),
            { message },
            onEvent,
            { signal },
        );
    }

    // TODO(PRODUCT-25807): wire once chatbot-broker stop-conversation endpoint ships
    public stopEvieConversation(rpName: string, conversationId: string): Promise<void> {
        return this.request('POST', getTprmUiRpUrl(rpName, `/evie/conversations/${conversationId}/stop-conversation`));
    }

    public renameEvieConversation(rpName: string, conversationId: string, title: string): Promise<void> {
        return this.request('PATCH', getTprmUiRpUrl(rpName, `/evie/conversations/${conversationId}`), { title });
    }

    public submitEvieFeedback(
        rpName: string,
        conversationId: string,
        body: { runId: string, score: 'UP' | 'DOWN' },
    ): Promise<void> {
        return this.request('POST', getTprmUiRpUrl(rpName, `/evie/conversations/${conversationId}/feedback`), body);
    }
}
